import { FlexBox, Skeleton } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { PercentageBar } from 'lib-analytics-ui';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useMemo } from 'react';

import {
  DashboardLegend,
  DashboardLegendItem,
} from 'app/views/layout/views/dashboard/components/legend';
import { useDashboardAgentPresenceMetric } from 'utils/dashboard';

import { useChannelDashboardChannelContext } from '../../../../channelDashboardChannel.context';
import { ChannelDashboardMetric } from '../../../metric/channelDashboardMetric';

type ChannelDashboardAgentPresenceMetricProps = PropsWithClassName;

export const ChannelDashboardAgentPresenceMetric = memo(
  function ChannelDashboardAgentPresenceMetric({
    className,
  }: ChannelDashboardAgentPresenceMetricProps): JSX.Element {
    const { media, metrics, isSkeleton } = useChannelDashboardChannelContext();

    const agentPresenceMetric = useDashboardAgentPresenceMetric(
      metrics?.agents,
    );

    const online = agentPresenceMetric.online.media_type[media];
    const available = agentPresenceMetric.available.media_type[media];

    // The number of available agents cannot be higher than the number of online agents. At best,
    // the numbers can be equal, but the count of available agents is a subset of those online.
    const percentage =
      online.total && available.total <= online.total
        ? Math.round((available.total / online.total) * 100)
        : 0;

    const legendItems: DashboardLegendItem[] = useMemo(
      () => [
        {
          type: 'number',
          title: 'Available',
          format: 'number',
          value: available.total,
          color: colors.purple['700'],
          linkProps: available.agents?.length
            ? {
                path: '/team',
                params: {
                  search: {
                    agent: available.agents,
                  },
                },
              }
            : undefined,
        },
        {
          type: 'number',
          title: 'Online',
          format: 'number',
          value: online.total,
          color: colors.neutral[200],
          linkProps: online.agents?.length
            ? {
                path: '/team',
                params: {
                  search: {
                    agent: online.agents,
                  },
                },
              }
            : undefined,
        },
      ],
      [available.total, online.total, available.agents, online.agents],
    );

    return (
      <ChannelDashboardMetric
        className={className}
        title="Agents presence"
        isSkeleton={isSkeleton}
        metricType="agent_presence"
      >
        <FlexBox flexDirection="column" gap="200" maxWidth="224px">
          {isSkeleton ? (
            <Skeleton height="20px" width="auto" />
          ) : (
            <PercentageBar
              value={percentage}
              showFullBar
              theme={
                [legendItems[0].color, legendItems[1].color] as [string, string]
              }
            />
          )}

          <DashboardLegend items={legendItems} isSkeleton={isSkeleton} />
        </FlexBox>
      </ChannelDashboardMetric>
    );
  },
);
